import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  ReceiptAttachmentModel,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import { ReceiptRecoveryCase } from 'src/models/transaction.models'
import { receiptAgentQueries } from 'src/queries/organizations/receiptAgent/receiptAgent.queries'

import {
  getReviewedReceiptAttachmentId,
  mergeReceiptRecoveryAttachments,
} from './receiptRecoveryPreview'

interface UseReceiptRecoveryAttachmentsParams {
  enabled: boolean
  organizationId?: string
  receiptRecoveryCase?: ReceiptRecoveryCase
  receiptAttachments: readonly ReceiptAttachmentModel[]
}

const isReceiptAttachmentResponse = (
  value: ReceiptAttachmentResponse | null | undefined,
): value is ReceiptAttachmentResponse =>
  value !== null &&
  value !== undefined &&
  typeof value.id === 'string' &&
  typeof value.urls === 'object' &&
  value.urls !== null

export const useReceiptRecoveryAttachments = ({
  enabled,
  organizationId,
  receiptRecoveryCase,
  receiptAttachments,
}: UseReceiptRecoveryAttachmentsParams) => {
  const receiptRecoveryCaseId =
    typeof receiptRecoveryCase?.id === 'string'
      ? receiptRecoveryCase.id
      : undefined
  const reviewedReceiptAttachmentId =
    getReviewedReceiptAttachmentId(receiptRecoveryCase)
  const shouldLoadDetachedCandidate =
    enabled &&
    organizationId !== undefined &&
    receiptRecoveryCaseId !== undefined &&
    reviewedReceiptAttachmentId !== undefined &&
    !receiptAttachments.some(({ id }) => id === reviewedReceiptAttachmentId)
  const { data: receiptRecoveryCaseDetail } = useQuery({
    ...receiptAgentQueries.case(organizationId, receiptRecoveryCaseId),
    enabled: shouldLoadDetachedCandidate,
  })
  const detachedReceiptRecoveryAttachment = useMemo(() => {
    const selectedReceipt = receiptRecoveryCaseDetail?.selectedReceipt

    if (
      !isReceiptAttachmentResponse(selectedReceipt) ||
      selectedReceipt.id !== reviewedReceiptAttachmentId
    ) {
      return undefined
    }

    return new ReceiptAttachmentModel(selectedReceipt)
  }, [reviewedReceiptAttachmentId, receiptRecoveryCaseDetail?.selectedReceipt])
  const receiptRecoveryAttachments = useMemo(
    () =>
      mergeReceiptRecoveryAttachments(
        receiptAttachments,
        detachedReceiptRecoveryAttachment,
      ),
    [receiptAttachments, detachedReceiptRecoveryAttachment],
  )

  return {
    detachedReceiptRecoveryAttachment,
    receiptRecoveryAttachments,
  }
}
